import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { PsychometricHeatmap } from "@/components/admin/PsychometricHeatmap";
import { NetworkVisualization } from "@/components/admin/NetworkVisualization";
import { ClusterAnalytics } from "@/components/admin/ClusterAnalytics";
import { NetworkSummary } from "@/components/admin/NetworkSummary";
import { SiteFilter } from "@/components/admin/SiteFilter";
import { toast } from "sonner";
import { Loader2, Play, RefreshCw, Zap, Users, AlertTriangle, Award, Network, BarChart3 } from "lucide-react";

interface AdminBehavioralProps {
  siteId?: string | null;
}

const AdminBehavioral = ({ siteId: propSiteId }: AdminBehavioralProps = {}) => {
  const [selectedSiteFilter, setSelectedSiteFilter] = useState<string>(propSiteId || 'all');
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState<string | null>(null);
  const [stats, setStats] = useState({
    totalWorkers: 0,
    highRiskWorkers: 0,
    safetyChampions: 0,
    totalConnections: 0,
    totalClusters: 0,
    infectedClusters: 0
  });

  // Use prop siteId if provided
  useEffect(() => {
    if (propSiteId) {
      setSelectedSiteFilter(propSiteId);
    }
  }, [propSiteId]);

  useEffect(() => {
    fetchStats();
  }, [selectedSiteFilter]);

  const fetchStats = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const { data: profile } = await supabase
        .from("worker_profiles")
        .select("company_id")
        .eq("user_id", session.user.id)
        .single();

      if (!profile?.company_id) return;

      const siteFilter = selectedSiteFilter !== 'all' ? selectedSiteFilter : null;

      // Build queries with optional site filter
      let workersQuery = supabase.from("worker_profiles").select("*", { count: "exact", head: true }).eq("company_id", profile.company_id);
      let highRiskQuery = supabase.from("worker_profiles").select("*", { count: "exact", head: true }).eq("company_id", profile.company_id).gt("current_risk_score", 60);
      let championsQuery = supabase.from("worker_profiles").select("*", { count: "exact", head: true }).eq("company_id", profile.company_id).eq("network_role", "champion");
      let connectionsQuery = supabase.from("worker_network_connections").select("*", { count: "exact", head: true }).eq("company_id", profile.company_id);
      let clustersQuery = supabase.from("worker_clusters").select("is_infected").eq("company_id", profile.company_id);

      if (siteFilter) {
        workersQuery = workersQuery.eq("site_id", siteFilter);
        highRiskQuery = highRiskQuery.eq("site_id", siteFilter);
        championsQuery = championsQuery.eq("site_id", siteFilter);
        // Note: connections and clusters may not have site_id, filter by workers
      }

      const [
        { count: totalWorkers },
        { count: highRiskWorkers },
        { count: safetyChampions },
        { count: totalConnections },
        { data: clusters }
      ] = await Promise.all([
        workersQuery,
        highRiskQuery,
        championsQuery,
        connectionsQuery,
        clustersQuery,
      ]);

      setStats({
        totalWorkers: totalWorkers || 0,
        highRiskWorkers: highRiskWorkers || 0,
        safetyChampions: safetyChampions || 0,
        totalConnections: totalConnections || 0,
        totalClusters: clusters?.length || 0,
        infectedClusters: clusters?.filter(c => c.is_infected).length || 0
      });
    } catch (error) {
      console.error("Error fetching behavioral stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const triggerBehavioralFunction = async (functionName: string, body: any = {}) => {
    setTriggering(functionName);
    try {
      const { data, error } = await supabase.functions.invoke(functionName, { body });
      
      if (error) throw error;
      
      toast.success(`${functionName} executed successfully`);
      await fetchStats();
    } catch (error) {
      console.error(`Error triggering ${functionName}:`, error);
      toast.error(`Failed to execute ${functionName}`, {
        description: error instanceof Error ? error.message : "Unknown error"
      });
    } finally {
      setTriggering(null);
    }
  };

  const triggerAllNetworkMetrics = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;

    const { data: profile } = await supabase
      .from("worker_profiles")
      .select("company_id")
      .eq("user_id", session.user.id)
      .single();

    if (!profile?.company_id) return;

    const { data: workers } = await supabase
      .from("worker_profiles")
      .select("id")
      .eq("company_id", profile.company_id);

    if (!workers) return;
    
    toast.info(`Calculating network metrics for ${workers.length} workers...`);
    
    for (const worker of workers) {
      await triggerBehavioralFunction("calculate-network-metrics", { workerId: worker.id });
    }
  };

  const triggerClusterDetection = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    
    const { data: profile } = await supabase
      .from("worker_profiles")
      .select("company_id")
      .eq("user_id", user.id)
      .single();
    
    if (!profile?.company_id) {
      toast.error("Company ID not found");
      return;
    }
    
    await triggerBehavioralFunction("detect-worker-clusters", { companyId: profile.company_id });
  };

  const triggerAllRiskPredictions = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;

    const { data: profile } = await supabase
      .from("worker_profiles")
      .select("company_id")
      .eq("user_id", session.user.id)
      .single();

    if (!profile?.company_id) return;

    const { data: workers } = await supabase
      .from("worker_profiles")
      .select("id")
      .eq("company_id", profile.company_id);

    if (!workers) return;
    
    toast.info(`Calculating risk scores for ${workers.length} workers...`);
    
    for (const worker of workers) {
      await triggerBehavioralFunction("predict-risk-score", { workerId: worker.id });
    }
  };

  const triggerAutomaticInterventions = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    
    const { data: profile } = await supabase
      .from("worker_profiles")
      .select("company_id")
      .eq("user_id", user.id)
      .single();
    
    if (!profile?.company_id) {
      toast.error("Company ID not found");
      return;
    }
    
    await triggerBehavioralFunction("trigger-automatic-interventions", { 
      companyId: profile.company_id,
      triggerEvent: 'weekly_check'
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-bold mb-2">Behavioral Science Analytics</h2>
          <p className="text-muted-foreground">
            Network analysis, psychometric profiles, and cluster detection based on Centola's Complex Contagion
          </p>
        </div>
        {/* Site Filter - only show if not passed from parent */}
        {!propSiteId && (
          <SiteFilter 
            value={selectedSiteFilter} 
            onChange={setSelectedSiteFilter}
            className="w-[200px]"
          />
        )}
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Users className="h-4 w-4" />
            Workers
          </div>
          <div className="text-2xl font-bold mt-1">{stats.totalWorkers}</div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <AlertTriangle className="h-4 w-4 text-destructive" />
            High Risk
          </div>
          <div className="text-2xl font-bold mt-1 text-destructive">{stats.highRiskWorkers}</div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Award className="h-4 w-4 text-success" />
            Champions
          </div>
          <div className="text-2xl font-bold mt-1 text-success">{stats.safetyChampions}</div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Network className="h-4 w-4 text-primary" />
            Connections
          </div>
          <div className="text-2xl font-bold mt-1">{stats.totalConnections}</div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Users className="h-4 w-4" />
            Clusters
          </div>
          <div className="text-2xl font-bold mt-1">{stats.totalClusters}</div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <AlertTriangle className="h-4 w-4 text-destructive" />
            Infected
          </div>
          <div className="text-2xl font-bold mt-1 text-destructive">{stats.infectedClusters}</div>
        </Card>
      </div>

      {/* Manual Trigger Controls */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
          <Zap className="h-5 w-5 text-primary" />
          Manual Behavioral Triggers
        </h3>
        <p className="text-sm text-muted-foreground mb-4">
          Manually execute behavioral science functions (also run automatically on events)
        </p>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Button
            variant="outline"
            onClick={triggerAllNetworkMetrics}
            disabled={triggering !== null}
            className="flex items-center gap-2"
          >
            {triggering === "calculate-network-metrics" ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Play className="h-4 w-4" />
            )}
            Network Metrics
          </Button>
          
          <Button
            variant="outline"
            onClick={triggerClusterDetection}
            disabled={triggering !== null}
            className="flex items-center gap-2"
          >
            {triggering === "detect-worker-clusters" ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Play className="h-4 w-4" />
            )}
            Detect Clusters
          </Button>
          
          <Button
            variant="outline"
            onClick={triggerAllRiskPredictions}
            disabled={triggering !== null}
            className="flex items-center gap-2"
          >
            {triggering === "predict-risk-score" ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            Risk Prediction
          </Button>
          
          <Button
            variant="outline"
            onClick={triggerAutomaticInterventions}
            disabled={triggering !== null}
            className="flex items-center gap-2"
          >
            {triggering === "trigger-automatic-interventions" ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Play className="h-4 w-4" />
            )}
            Auto Interventions
          </Button>
        </div>
      </Card>

      {/* Main Analytics Tabs - 4 tabs including Summary */}
      <Tabs defaultValue="summary" className="space-y-4">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="summary" className="gap-1">
            <BarChart3 className="h-4 w-4" />
            Summary
          </TabsTrigger>
          <TabsTrigger value="network">Network Map</TabsTrigger>
          <TabsTrigger value="clusters">Clusters</TabsTrigger>
          <TabsTrigger value="psychometric">Psychometric</TabsTrigger>
        </TabsList>

        <TabsContent value="summary">
          <Card className="p-6">
            <NetworkSummary siteId={propSiteId || (selectedSiteFilter !== 'all' ? selectedSiteFilter : undefined)} />
          </Card>
        </TabsContent>

        <TabsContent value="network">
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-2">Worker Network Map</h3>
            <p className="text-sm text-muted-foreground mb-6">
              Interactive network visualization showing worker connections, roles, and risk levels
            </p>
            <NetworkVisualization siteId={propSiteId || (selectedSiteFilter !== 'all' ? selectedSiteFilter : undefined)} />
          </Card>
        </TabsContent>

        <TabsContent value="clusters">
          <Card className="p-6">
            <ClusterAnalytics siteId={propSiteId || (selectedSiteFilter !== 'all' ? selectedSiteFilter : undefined)} />
          </Card>
        </TabsContent>

        <TabsContent value="psychometric">
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Psychometric Profile Distribution</h3>
            <p className="text-sm text-muted-foreground mb-6">
              UPPS-P impulsivity facets across workforce. Focus on <strong>Lack of Perseverance</strong> (key accident predictor).
            </p>
            <PsychometricHeatmap siteId={propSiteId || (selectedSiteFilter !== 'all' ? selectedSiteFilter : undefined)} />
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminBehavioral;
